import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import useQuery from "utils/hooks/useQuery.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { ConnectExternalAccountStep } from "@shared/features/configuration/types.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { useExternalInviteData } from "features/configuration/InviteExternalUsers/hooks.tsx";
import { useSelector } from "react-redux";
import { UserImpersonationPermissionLevel } from "@shared/types";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import {
  BASE_REGISTER_PATH,
  EXTERNAL_INVITE_CONFIRM_USER_PATH,
  EXTERNAL_INVITE_SUCCESS_PATH,
  LOGIN_PATH,
  ONBOARDING_CONNECT_EXCHANGE_PATH,
  ONBOARDING_SELECT_EXCHANGE_PATH,
  ONBOARDING_VERIFY_EMAIL_PATH,
  RESET_PASSWORD_PATH,
} from "common/constants.ts";
import { useRouteMatch } from "react-router-dom";
import {
  setExternalInviteLocalStorageLogin,
  validateInviteLocalStorageLogin,
} from "features/configuration/common/utils.ts";
import {
  useGetUserQuery,
  useSelectRealUser,
} from "@shared/utils/hooks/misc.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

export const ExternalConnectAccountProvider = ({
  children,
}: PropsWithChildren) => {
  const query = useQuery();
  const { historyPush } = useHistoryWithQuery();
  const exchange = query.get("exchange") ?? undefined;
  const { getAccountInviteStatus, externalInviteToken } =
    useExternalInviteData();
  const { brandingData } = useBranding();
  const {
    data: inviteData,
    isLoading,
    isError,
    error,
  } = getAccountInviteStatus;
  const hasReadAccess =
    inviteData?.permission_level !== UserImpersonationPermissionLevel.NONE;

  const authRefreshToken = useSelector(
    sharedLib.selectors.authentication.selectRefreshToken
  );
  const getRealUserQuery = useGetUserQuery(false);

  const [confirmedUserId, setConfirmedUserId] = useState<number>();
  const [initialized, setInitialized] = useState(false);
  const user = useSelectRealUser();
  const isSubUser = !!user?.is_sub_user;
  const isUserNotSubUser = !!user && !isSubUser;

  const isLoginPath = !!useRouteMatch(LOGIN_PATH);
  const isResetPasswordPath = !!useRouteMatch(RESET_PASSWORD_PATH);
  const isSelectExchangePath = !!useRouteMatch(ONBOARDING_SELECT_EXCHANGE_PATH);
  const isConnectExchangePath = !!useRouteMatch(
    ONBOARDING_CONNECT_EXCHANGE_PATH
  );
  const isSuccessPath = !!useRouteMatch(EXTERNAL_INVITE_SUCCESS_PATH);

  const isUserLinkedToAnotherManager =
    isSubUser &&
    !!user.asset_manager_id &&
    user.asset_manager_id !== inviteData?.inviter_id;
  const isUserWithDifferencePermission =
    isSubUser &&
    user.sub_user_permission_level !== UserImpersonationPermissionLevel.NONE &&
    user.sub_user_permission_level !== inviteData?.permission_level;
  const confirmSubUserRequired =
    isSubUser &&
    confirmedUserId !== user?.id &&
    !validateInviteLocalStorageLogin(user?.id);
  const redirectAuthStep = hasReadAccess && !isSubUser;
  const redirectVerifyEmail = hasReadAccess && user && !user.is_email_verified;
  const redirectConfirmUserStep =
    hasReadAccess &&
    (confirmSubUserRequired ||
      isUserNotSubUser ||
      isUserLinkedToAnotherManager ||
      isUserWithDifferencePermission);
  const redirectConnectExchangeStep = isConnectExchangePath || !!exchange;

  const getStep = () => {
    if (isSelectExchangePath) return ConnectExternalAccountStep.SELECT_EXCHANGE;
    if (isConnectExchangePath)
      return ConnectExternalAccountStep.CONNECT_EXCHANGE;
    if (isSuccessPath) return ConnectExternalAccountStep.SUCCESS;
    return ConnectExternalAccountStep.CREATE_ACCOUNT;
  };

  const step = getStep();

  useEffect(() => {
    if (authRefreshToken && !getRealUserQuery.isFetching) {
      try {
        getRealUserQuery.refetch();
      } catch {
        return;
      }
    }
  }, [authRefreshToken]);

  useEffect(() => {
    if (!inviteData || getRealUserQuery.isFetching || isSuccessPath) return;
    if (redirectVerifyEmail) {
      historyPush(ONBOARDING_VERIFY_EMAIL_PATH);
    } else if (redirectConfirmUserStep) {
      historyPush(EXTERNAL_INVITE_CONFIRM_USER_PATH);
    } else if (redirectConnectExchangeStep) {
      historyPush(ONBOARDING_CONNECT_EXCHANGE_PATH);
    } else if (redirectAuthStep) {
      if (!isLoginPath && !isResetPasswordPath) {
        historyPush(BASE_REGISTER_PATH);
      }
    } else {
      historyPush(ONBOARDING_SELECT_EXCHANGE_PATH);
    }
    setInitialized(true);
  }, [
    inviteData,
    getRealUserQuery.isFetching,
    redirectVerifyEmail,
    redirectConnectExchangeStep,
    redirectConfirmUserStep,
    redirectAuthStep,
    isLoginPath,
    isResetPasswordPath,
  ]);

  const getContent = () => {
    return (
      <LoadingSpinnerOverlay
        isLoading={isLoading || (!initialized && !isError)}
        hideContentWhileLoading={true}
        spinnerSize="large"
        spinnerMarginTop={150}
      >
        {children}
      </LoadingSpinnerOverlay>
    );
  };

  const errorMessage = error?.data?.message;

  const handleAuthCompleted = () => {
    setExternalInviteLocalStorageLogin();
    setConfirmedUserId(undefined);
  };

  const handleUserConfirmed = (userId?: number) => {
    setExternalInviteLocalStorageLogin(userId);
    setConfirmedUserId(userId);
  };

  const value = useMemo(() => {
    return {
      isExternalConnectAccount: true,
      accountName: inviteData?.account_prefix,
      token: externalInviteToken,
      exchange,
      step,
      isError,
      errorMessage,
      isWhitelabel: brandingData?.whitelabel,
      inviteData,
      hasReadAccess,
      isUserLinkedToAnotherManager,
      isUserNotSubUser,
      isUserWithDifferencePermission,
      handleAuthCompleted,
      handleUserConfirmed,
      hideQuickConnect: brandingData?.hide_quick_connect,
    };
  }, [
    inviteData,
    externalInviteToken,
    step,
    exchange,
    errorMessage,
    isError,
    isLoading,
    hasReadAccess,
    isUserLinkedToAnotherManager,
    isUserNotSubUser,
    isUserWithDifferencePermission,
    brandingData,
  ]);

  return (
    <ExternalConnectAccountContext.Provider value={value}>
      {getContent()}
    </ExternalConnectAccountContext.Provider>
  );
};

export default ExternalConnectAccountProvider;
